import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ClientLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Upload,
  FileText,
  Shield,
  Loader2,
  ArrowLeft,
  Save,
  CheckCircle2,
  Clock,
  Trash2,
  FileSpreadsheet,
  Eye,
  Lock,
  ChevronRight,
  Circle,
  AlertTriangle,
  Copy
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- List View ---
function AssessmentList({ assessments, loading, onUpload, onOpen, onDelete }) {
  const [deleteId, setDeleteId] = useState(null);
  const pendingCount = assessments.filter(a => a.status === 'pending_review').length;
  const activeCount = assessments.filter(a => a.status === 'active').length;
  const completedCount = assessments.filter(a => a.status === 'completed').length;

  const statusLabel = (s) => {
    if (s === 'completed') return { text: 'Completed', cls: 'bg-emerald-100 text-emerald-700' };
    if (s === 'active') return { text: 'In Progress', cls: 'bg-amber-100 text-amber-700' };
    return { text: 'Pending Review', cls: 'bg-blue-100 text-blue-700' };
  };

  return (
    <>
      {/* Privacy banner */}
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-[#004E98]/20 bg-[#004E98]/5 p-4" data-testid="privacy-banner">
        <Lock className="h-5 w-5 text-[#004E98] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-[#004E98]">Private &amp; Encrypted</p>
          <p className="text-xs text-slate-600 mt-0.5">
            Assessments are encrypted with your company key. Admins cannot view this data. Uploaded files are permanently deleted after extraction.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="stat-card" data-testid="stat-pending">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Eye className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="stat-value">{pendingCount}</div>
              <div className="stat-label">Pending Review</div>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-active">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <div className="stat-value">{activeCount}</div>
              <div className="stat-label">In Progress</div>
            </div>
          </div>
        </div>
        <div className="stat-card" data-testid="stat-completed-assessments">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <div className="stat-value">{completedCount}</div>
              <div className="stat-label">Completed</div>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Private Assessments
          </h2>
          <Button className="btn-primary px-4 py-2" onClick={onUpload} data-testid="upload-assessment-btn">
            <Upload className="h-4 w-4 mr-2" />
            Upload Assessment
          </Button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading assessments...</p>
          </div>
        ) : assessments.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-assessments">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No assessments yet</h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Upload a vendor security assessment (Excel, Word, or PDF) and our AI will extract the questions into a fillable form.
            </p>
            <Button className="btn-primary" onClick={onUpload} data-testid="empty-upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload Your First Assessment
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {assessments.map((a) => {
              const st = statusLabel(a.status);
              return (
                <div key={a.id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`assessment-row-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        a.status === 'completed' ? 'bg-emerald-100' : a.status === 'active' ? 'bg-amber-100' : 'bg-blue-100'
                      }`}>
                        {a.status === 'completed' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> :
                         a.status === 'active' ? <Clock className="h-5 w-5 text-amber-600" /> :
                         <Eye className="h-5 w-5 text-blue-600" />}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-slate-900 truncate">{a.title}</h3>
                        <p className="text-sm text-slate-500 truncate">
                          {a.description || 'No description'}
                          <span className="ml-2 text-xs text-slate-400">
                            {new Date(a.created_at).toLocaleDateString()}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${st.cls}`}>{st.text}</span>
                      <Button variant="ghost" size="sm" onClick={() => onOpen(a.id)} data-testid={`open-assessment-${a.id}`}>
                        {a.status === 'pending_review' ? 'Review' : a.status === 'active' ? 'Continue' : 'View'}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteId(a.id)}
                        data-testid={`delete-assessment-${a.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this assessment and all associated data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-assessment">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-assessment"
              onClick={() => { onDelete(deleteId); setDeleteId(null); }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// --- Upload View ---
function UploadView({ onBack, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'xlsx'].includes(ext)) {
      toast.error('Only PDF, Word (.docx), and Excel (.xlsx) files are supported');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size must be under 25MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API_URL}/assessments/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(`Extracted ${res.data.field_count} questions from "${res.data.title}"`);
      onUploaded(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); };

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#FF6B35] mb-6 transition-colors" data-testid="back-to-list">
        <ArrowLeft className="h-4 w-4" /> Back to Assessments
      </button>
      <PageHeader title="Upload Assessment" description="Upload a vendor security assessment (Excel, Word, or PDF). Our AI will extract the questions into a fillable form." />

      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-[#FF6B35] bg-[#FF6B35]/5' : 'border-slate-300 hover:border-[#FF6B35]/50 bg-white'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        data-testid="upload-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.xlsx"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {uploading ? (
          <div>
            <Loader2 className="h-12 w-12 text-[#FF6B35] animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">Processing document...</p>
            <p className="text-sm text-slate-500 mt-2">AI is extracting questions. This may take a moment.</p>
          </div>
        ) : (
          <div>
            <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900">Drag &amp; drop your file here</p>
            <p className="text-sm text-slate-500 mt-2">or click to browse. Supports PDF, Word (.docx), Excel (.xlsx) up to 25MB</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-amber-800">File Security Notice</p>
          <p className="text-xs text-amber-700 mt-0.5">
            The uploaded file is processed in memory only. It is never stored on disk or in any database. After extraction, the file data is permanently discarded.
          </p>
        </div>
      </div>
    </div>
  );
}

// --- Assessment Form View ---
function AssessmentForm({ assessmentId, onBack, onRefreshList }) {
  const [assessment, setAssessment] = useState(null);
  const [formData, setFormData] = useState({});
  const [fieldAssignments, setFieldAssignments] = useState({});
  const [userDepartments, setUserDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const [vendorLinkCopied, setVendorLinkCopied] = useState(false);
  const saveTimeout = useRef(null);

  useEffect(() => {
    loadAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const loadAssessment = async () => {
    try {
      const [assessRes, meRes] = await Promise.all([
        axios.get(`${API_URL}/assessments/${assessmentId}`),
        axios.get(`${API_URL}/auth/me`),
      ]);
      setAssessment(assessRes.data);
      setFormData(assessRes.data.data || {});
      setFieldAssignments(assessRes.data.field_assignments || {});
      setUserDepartments(meRes.data.departments || []);
    } catch (err) {
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldName]: value };
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(() => autoSave(updated, null), 2000);
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const autoSave = async (data, assignments) => {
    try {
      const payload = { data };
      if (assignments) payload.field_assignments = assignments;
      await axios.put(`${API_URL}/assessments/${assessmentId}`, payload);
      setLastSaved(new Date());
    } catch {
      // Silently fail auto-save
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/assessments/${assessmentId}`, { data: formData, field_assignments: fieldAssignments });
      setLastSaved(new Date());
      toast.success('Progress saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignmentChange = useCallback((fieldName, assignee) => {
    setFieldAssignments(prev => {
      const updated = { ...prev, [fieldName]: assignee || null };
      // Filter out null/empty assignments
      Object.keys(updated).forEach(k => { if (!updated[k]) delete updated[k]; });
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(() => autoSave(formData, updated), 2000);
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, formData]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API_URL}/assessments/${assessmentId}/confirm`);
      toast.success('Assessment confirmed. You can now start filling it out.');
      loadAssessment();
      onRefreshList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleComplete = async () => {
    // Save first
    try {
      await axios.put(`${API_URL}/assessments/${assessmentId}`, { data: formData });
    } catch { /* */ }
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/assessments/${assessmentId}/complete`);
      toast.success('Assessment marked as completed');
      loadAssessment();
      onRefreshList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete');
    } finally {
      setCompleting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${API_URL}/assessments/${assessmentId}/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${assessment.title.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportExcel = async (fmt) => {
    const exportFmt = fmt || assessment.default_export_format || 'auditguard';
    try {
      const response = await axios.get(`${API_URL}/assessments/${assessmentId}/export/excel?format=${exportFmt}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = exportFmt === 'original' ? '_original' : '';
      link.setAttribute('download', `${assessment.title.replace(/ /g, '_')}${suffix}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Excel downloaded (${exportFmt === 'original' ? 'Original Format' : 'AuditGuard Format'})`);
    } catch {
      toast.error('Failed to export Excel');
    }
  };

  const handleToggleExportFormat = async (fmt) => {
    try {
      await axios.put(`${API_URL}/assessments/${assessmentId}/export-format`, { default_export_format: fmt });
      setAssessment(prev => ({ ...prev, default_export_format: fmt }));
      toast.success(`Default export set to ${fmt === 'original' ? 'Original Format' : 'AuditGuard Format'}`);
    } catch {
      toast.error('Failed to update export format');
    }
  };

  const copyVendorLink = () => {
    if (!assessment?.vendor_token) return;
    const link = `${window.location.origin}/assessment-vendor/${assessment.vendor_token}`;
    navigator.clipboard.writeText(link);
    setVendorLinkCopied(true);
    toast.success('Vendor link copied to clipboard');
    setTimeout(() => setVendorLinkCopied(false), 3000);
  };

  const copyDeptLink = (deptName) => {
    const token = assessment?.department_tokens?.[deptName];
    if (!token) return;
    const link = `${window.location.origin}/assessment-department/${token}`;
    navigator.clipboard.writeText(link);
    toast.success(`${deptName} department link copied!`);
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500">Loading assessment...</p>
      </div>
    );
  }

  if (!assessment) {
    return <div className="p-12 text-center text-slate-500">Assessment not found.</div>;
  }

  const sections = assessment.sections || [];
  const section = sections[currentSection];
  const totalFields = sections.reduce((acc, s) => acc + (s.fields?.length || 0), 0);
  const filledFields = Object.keys(formData).filter(k => formData[k] && String(formData[k]).trim()).length;
  const progress = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  const isReadOnly = assessment.status === 'completed';
  const isPendingReview = assessment.status === 'pending_review';

  return (
    <div>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#FF6B35] transition-colors" data-testid="back-from-form">
          <ArrowLeft className="h-4 w-4" /> Back to Assessments
        </button>
        <div className="flex items-center gap-3">
          {lastSaved && (
            <span className="text-xs text-slate-400" data-testid="last-saved-time">
              Saved {lastSaved.toLocaleTimeString()}
            </span>
          )}
          {!isPendingReview && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="save-assessment-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExportExcel()} data-testid="export-excel-btn">
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel{assessment.default_export_format === 'original' ? ' (Original)' : ''}
          </Button>
        </div>
      </div>

      {/* Vendor/Department assignment summary */}
      {!isPendingReview && Object.keys(fieldAssignments).length > 0 && (
        <div className="mb-4 space-y-2" data-testid="assignment-summary">
          {assessment.vendor_fields?.length > 0 && (
            <div className="flex items-center gap-3 rounded-lg border border-purple-200 bg-purple-50/50 p-3">
              <div className="w-3 h-3 rounded-full bg-purple-500 flex-shrink-0" />
              <span className="text-sm text-purple-800 flex-1">
                <strong>{assessment.vendor_fields.length}</strong> field(s) assigned to Vendor/MSP
              </span>
              <Button size="sm" variant="ghost" onClick={copyVendorLink} className="text-purple-700 hover:text-purple-900" data-testid="copy-vendor-link">
                {vendorLinkCopied ? <CheckCircle2 className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                {vendorLinkCopied ? 'Copied!' : 'Copy Vendor Link'}
              </Button>
              {assessment.vendor_completed && (
                <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Completed</span>
              )}
            </div>
          )}
          {Object.entries(assessment.department_tokens || {}).map(([deptName, token]) => {
            const deptFields = Object.entries(fieldAssignments).filter(([, v]) => v === deptName);
            const isComplete = assessment.department_completed?.[deptName];
            return (
              <div key={deptName} className="flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50/50 p-3">
                <div className="w-3 h-3 rounded-full bg-teal-500 flex-shrink-0" />
                <span className="text-sm text-teal-800 flex-1">
                  <strong>{deptFields.length}</strong> field(s) assigned to <strong>{deptName}</strong>
                </span>
                <Button size="sm" variant="ghost" onClick={() => copyDeptLink(deptName)} className="text-teal-700 hover:text-teal-900" data-testid={`copy-dept-link-${deptName}`}>
                  <Copy className="h-4 w-4 mr-1" /> Copy Link
                </Button>
                {isComplete && (
                  <span className="text-xs font-medium bg-teal-100 text-teal-700 px-2 py-1 rounded-full">Completed</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Title & progress */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="assessment-title">
          {assessment.title}
        </h1>
        {assessment.description && <p className="text-slate-600 mt-1">{assessment.description}</p>}

        {/* Requesting Company Metadata */}
        {assessment.metadata && Object.keys(assessment.metadata).length > 0 && (
          <div className="mt-4 bg-slate-50 rounded-lg border border-slate-200 p-4" data-testid="assessment-metadata">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Assessment Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(assessment.metadata).map(([key, val]) => (
                <div key={key} className="text-sm">
                  <span className="font-medium text-slate-700">{key}:</span>{' '}
                  <span className="text-slate-600">{val}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions (collapsible) */}
        {assessment.instructions && (
          <details className="mt-4 bg-blue-50/50 rounded-lg border border-blue-200/50" data-testid="assessment-instructions">
            <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-[#004E98] hover:bg-blue-50 rounded-lg flex items-center gap-2">
              <FileText className="h-4 w-4" />
              View Assessment Instructions
            </summary>
            <div className="px-4 pb-4 text-sm text-slate-600 whitespace-pre-line leading-relaxed max-h-80 overflow-y-auto">
              {assessment.instructions}
            </div>
          </details>
        )}

        <div className="mt-4 flex items-center gap-4">
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-500">Progress</span>
              <span className="font-medium text-[#FF6B35]">{progress}% ({filledFields}/{totalFields})</span>
            </div>
            <div className="progress-bar h-2">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            isReadOnly ? 'bg-emerald-100 text-emerald-700' :
            isPendingReview ? 'bg-blue-100 text-blue-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {isReadOnly ? 'Completed' : isPendingReview ? 'Pending Review' : 'In Progress'}
          </span>
        </div>
      </div>

      {/* Pending review banner */}
      {isPendingReview && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5" data-testid="review-banner">
          <div className="flex items-start gap-3">
            <Eye className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-blue-900">Review Extracted Questions</p>
              <p className="text-sm text-blue-700 mt-1">
                The AI has extracted {totalFields} questions from your document into {sections.length} sections.
                Review them below and confirm to begin filling out the assessment.
              </p>
              <Button
                className="mt-3 bg-[#004E98] hover:bg-[#003d7a] text-white"
                onClick={handleConfirm}
                disabled={confirming}
                data-testid="confirm-assessment-btn"
              >
                {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Confirm &amp; Start Filling
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Section nav + form */}
      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <div className="bg-white rounded-xl border border-slate-200 sticky top-20 max-h-[calc(100vh-6rem)] flex flex-col">
            <div className="p-4 pb-0">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Sections</h3>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-2 space-y-1">
              {sections.map((s, idx) => {
                const sectionFields = s.fields || [];
                const sectionFilled = sectionFields.filter(f => formData[f.name] && String(formData[f.name]).trim()).length;
                const allFilled = sectionFilled === sectionFields.length && sectionFields.length > 0;
                return (
                  <button
                    key={s.id || idx}
                    onClick={() => setCurrentSection(idx)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      idx === currentSection
                        ? 'bg-[#FF6B35]/10 text-[#FF6B35] font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`section-nav-${idx}`}
                  >
                    {allFilled ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    ) : sectionFilled > 0 ? (
                      <Clock className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" />
                    )}
                    <span className="truncate">{s.title}</span>
                    <span className="ml-auto text-xs text-slate-400">{sectionFilled}/{sectionFields.length}</span>
                  </button>
                );
              })}
            </div>

            {/* Export Format Preference */}
            {assessment.original_structure && (
              <div className="p-4 pt-3 border-t border-slate-200 flex-shrink-0" data-testid="export-format-selector">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Export Format</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleToggleExportFormat('original')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors border ${
                      assessment.default_export_format === 'original'
                        ? 'border-[#FF6B35] bg-[#FF6B35]/5 text-[#FF6B35] font-medium'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="export-format-original"
                  >
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Original Format</div>
                        <div className="text-xs opacity-70 mt-0.5">Matches the uploaded document structure</div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleToggleExportFormat('auditguard')}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors border ${
                      assessment.default_export_format === 'auditguard'
                        ? 'border-[#FF6B35] bg-[#FF6B35]/5 text-[#FF6B35] font-medium'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="export-format-auditguard"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 flex-shrink-0" />
                      <div>
                        <div className="font-medium">AuditGuard Format</div>
                        <div className="text-xs opacity-70 mt-0.5">Our branded template layout</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="col-span-12 lg:col-span-9">
          {section && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="assessment-form-section">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {section.title}
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Section {currentSection + 1} of {sections.length}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {(section.fields || []).map((field, fIdx) => {
                  const assignee = fieldAssignments[field.name] || '';
                  const isVendorField = assignee === '__vendor__';
                  const isDeptField = assignee && assignee !== '__vendor__';
                  const isAssigned = isVendorField || isDeptField;
                  const borderCls = isVendorField ? 'border-purple-300 bg-purple-50/30' : isDeptField ? 'border-teal-300 bg-teal-50/30' : 'border-slate-100 bg-slate-50/50';

                  return (
                  <div key={field.name || fIdx} className={`rounded-lg border p-4 ${borderCls}`} data-testid={`field-${field.name}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <Label className="text-sm font-medium text-slate-800 leading-relaxed">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                      </div>
                      {/* Assignment dropdown */}
                      {!isPendingReview && !isReadOnly && (
                        <Select
                          value={assignee || 'self'}
                          onValueChange={(v) => handleAssignmentChange(field.name, v === 'self' ? null : v)}
                        >
                          <SelectTrigger className="w-[160px] h-8 text-xs flex-shrink-0" data-testid={`assign-${field.name}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="self">Self</SelectItem>
                            <SelectItem value="__vendor__">Vendor/MSP</SelectItem>
                            {userDepartments.map(dept => (
                              <SelectItem key={dept.name} value={dept.name}>{dept.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {(isPendingReview || isReadOnly) && isAssigned && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${
                          isVendorField ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'
                        }`}>
                          {isVendorField ? 'Vendor/MSP' : assignee}
                        </span>
                      )}
                    </div>
                    {field.description && (
                      <p className="text-xs text-slate-500 mb-2 leading-relaxed">{field.description}</p>
                    )}
                    {field.question_type && (
                      <span className="inline-block text-[10px] font-medium text-slate-400 bg-slate-200/80 px-1.5 py-0.5 rounded mb-2 uppercase">{field.question_type}</span>
                    )}

                    {/* Combo: dropdown options + text/justification input */}
                    {(field.type === 'combo' || field.type === 'select') && field.options && field.options.length > 0 ? (
                      <div className="space-y-2">
                        <Select
                          value={formData[field.name + '_selection'] || formData[field.name] || ''}
                          onValueChange={(v) => {
                            handleFieldChange(field.name + '_selection', v);
                            if (!formData[field.name + '_text']) {
                              handleFieldChange(field.name, v);
                            }
                          }}
                          disabled={isReadOnly || isPendingReview}
                        >
                          <SelectTrigger data-testid={`select-${field.name}`} className="bg-white">
                            <SelectValue placeholder="Select a response..." />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <RichTextEditor
                          value={formData[field.name + '_text'] || ''}
                          onChange={(html) => {
                            handleFieldChange(field.name + '_text', html);
                            const sel = formData[field.name + '_selection'] || '';
                            const plainText = html.replace(/<[^>]+>/g, '').trim();
                            const combined = plainText ? (sel ? `${sel} — ${plainText}` : plainText) : sel;
                            handleFieldChange(field.name, combined);
                          }}
                          placeholder="Additional details or justification..."
                          readOnly={isReadOnly || isPendingReview}
                          data-testid={`text-${field.name}`}
                        />
                      </div>
                    ) : field.type === 'textarea' ? (
                      <RichTextEditor
                        value={formData[field.name] || ''}
                        onChange={(html) => handleFieldChange(field.name, html)}
                        placeholder="Enter your response..."
                        readOnly={isReadOnly || isPendingReview}
                        data-testid={`textarea-${field.name}`}
                      />
                    ) : (
                      <Input
                        value={formData[field.name] || ''}
                        onChange={(e) => handleFieldChange(field.name, e.target.value)}
                        placeholder="Enter your response..."
                        readOnly={isReadOnly || isPendingReview}
                        className="bg-white"
                        data-testid={`input-${field.name}`}
                      />
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
                  disabled={currentSection === 0}
                  data-testid="prev-section-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> Previous
                </Button>
                <div className="flex items-center gap-3">
                  {currentSection === sections.length - 1 && !isReadOnly && !isPendingReview && (
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={handleComplete}
                      disabled={completing}
                      data-testid="complete-assessment-btn"
                    >
                      {completing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Mark Complete
                    </Button>
                  )}
                  <Button
                    onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
                    disabled={currentSection === sections.length - 1}
                    className="btn-primary"
                    data-testid="next-section-btn"
                  >
                    Next <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Vendor/Department Portal View (accessed via token) ---
export function PortalAssessmentView({ token, portalType }) {
  const [assessment, setAssessment] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeout = useRef(null);

  const apiPath = portalType === 'vendor' ? `vendor/${token}` : `department/${token}`;

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/assessments/${apiPath}`);
        setAssessment(res.data);
        setFormData(res.data.data || {});
      } catch {
        toast.error('Invalid or expired link');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, apiPath]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldName]: value };
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(() => {
        axios.put(`${API_URL}/assessments/${apiPath}`, { data: updated }).then(() => {
          setLastSaved(new Date());
        }).catch(() => {});
      }, 2000);
      return updated;
    });
  }, [apiPath]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/assessments/${apiPath}`, { data: formData });
      setLastSaved(new Date());
      toast.success('Progress saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    await handleSave();
    setCompleting(true);
    try {
      await axios.post(`${API_URL}/assessments/${apiPath}/complete`);
      toast.success('Your section has been marked as complete!');
    } catch {
      toast.error('Failed to mark as complete');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Link Not Found</h2>
          <p className="text-slate-500 mt-2">This {portalType} link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  // Filter to only show assigned fields
  const fieldAssignments = assessment.field_assignments || {};
  const deptName = assessment._dept_name;
  const assignedFieldNames = new Set(
    portalType === 'vendor'
      ? (assessment.vendor_fields || [])
      : Object.entries(fieldAssignments).filter(([, v]) => v === deptName).map(([k]) => k)
  );

  // Filter sections to only include ones with assigned fields
  const allSections = assessment.sections || [];
  const filteredSections = allSections
    .map(s => ({
      ...s,
      fields: (s.fields || []).filter(f => assignedFieldNames.has(f.name))
    }))
    .filter(s => s.fields.length > 0);

  const section = filteredSections[currentSection];
  const portalLabel = portalType === 'vendor' ? 'Vendor/MSP' : deptName || 'Department';
  const portalColor = portalType === 'vendor' ? 'purple' : 'teal';
  const isComplete = portalType === 'vendor'
    ? assessment.vendor_completed
    : assessment.department_completed?.[deptName];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className={`mb-6 rounded-xl border-2 border-${portalColor}-200 bg-${portalColor}-50/50 p-5`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 bg-${portalColor}-100 rounded-lg flex items-center justify-center`}>
              <Shield className={`h-5 w-5 text-${portalColor}-600`} />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {assessment.title}
              </h1>
              <p className="text-sm text-slate-600">
                {portalLabel} Portal — {assignedFieldNames.size} field(s) assigned across {filteredSections.length} section(s)
              </p>
            </div>
            <div className="flex items-center gap-2">
              {lastSaved && <span className="text-xs text-slate-400">Saved {lastSaved.toLocaleTimeString()}</span>}
              {!isComplete && (
                <>
                  <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} data-testid="portal-save-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleComplete} disabled={completing} data-testid="portal-complete-btn">
                    {completing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                    Mark Complete
                  </Button>
                </>
              )}
              {isComplete && (
                <span className="text-sm font-medium bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full">Completed</span>
              )}
            </div>
          </div>
        </div>

        {/* Section nav */}
        {filteredSections.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {filteredSections.map((s, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSection(idx)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  idx === currentSection
                    ? 'bg-[#FF6B35] text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {s.title} ({s.fields.length})
              </button>
            ))}
          </div>
        )}

        {/* Form */}
        {section && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-1">{section.title}</h2>
            <p className="text-sm text-slate-500 mb-6">Section {currentSection + 1} of {filteredSections.length}</p>
            <div className="space-y-6">
              {section.fields.map((field, fIdx) => (
                <div key={field.name || fIdx} className="rounded-lg border border-slate-100 bg-slate-50/50 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{field.label}</Label>
                  {field.description && <p className="text-xs text-slate-500 mb-2">{field.description}</p>}
                  {(field.type === 'combo' || field.type === 'select') && field.options?.length > 0 ? (
                    <div className="space-y-2">
                      <Select
                        value={formData[field.name + '_selection'] || formData[field.name] || ''}
                        onValueChange={(v) => {
                          setFormData(prev => {
                            const u = { ...prev, [field.name + '_selection']: v };
                            if (!prev[field.name + '_text']) u[field.name] = v;
                            if (saveTimeout.current) clearTimeout(saveTimeout.current);
                            saveTimeout.current = setTimeout(() => {
                              axios.put(`${API_URL}/assessments/${apiPath}`, { data: u }).catch(() => {});
                            }, 2000);
                            return u;
                          });
                        }}
                        disabled={isComplete}
                      >
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {field.options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <RichTextEditor
                        value={formData[field.name + '_text'] || ''}
                        onChange={(html) => handleFieldChange(field.name + '_text', html)}
                        placeholder="Additional details..."
                        readOnly={isComplete}
                      />
                    </div>
                  ) : field.type === 'textarea' ? (
                    <RichTextEditor value={formData[field.name] || ''} onChange={(html) => handleFieldChange(field.name, html)} placeholder="Enter your response..." readOnly={isComplete} />
                  ) : (
                    <Input value={formData[field.name] || ''} onChange={(e) => handleFieldChange(field.name, e.target.value)} placeholder="Enter your response..." readOnly={isComplete} className="bg-white" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" onClick={() => setCurrentSection(Math.max(0, currentSection - 1))} disabled={currentSection === 0}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Previous
              </Button>
              <Button onClick={() => setCurrentSection(Math.min(filteredSections.length - 1, currentSection + 1))} disabled={currentSection === filteredSections.length - 1} className="btn-primary">
                Next <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main Page ---
export default function AssessmentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  const view = searchParams.get('view') || 'list';
  const activeId = searchParams.get('id');

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const res = await axios.get(`${API_URL}/assessments`);
      setAssessments(res.data);
    } catch {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const goToList = () => setSearchParams({});
  const goToUpload = () => setSearchParams({ view: 'upload' });
  const goToForm = (id) => setSearchParams({ view: 'form', id });

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/assessments/${id}`);
      setAssessments(prev => prev.filter(a => a.id !== id));
      toast.success('Assessment deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <ClientLayout>
      <div className="page-container py-8">
        {view === 'list' && (
          <>
            <PageHeader
              title="Private Assessments"
              description="Upload and manage vendor security assessments. Encrypted and private to your organization."
            />
            <AssessmentList
              assessments={assessments}
              loading={loading}
              onUpload={goToUpload}
              onOpen={goToForm}
              onDelete={handleDelete}
            />
          </>
        )}
        {view === 'upload' && (
          <UploadView
            onBack={goToList}
            onUploaded={(id) => {
              fetchAssessments();
              goToForm(id);
            }}
          />
        )}
        {view === 'form' && activeId && (
          <AssessmentForm
            assessmentId={activeId}
            onBack={goToList}
            onRefreshList={fetchAssessments}
          />
        )}
      </div>
    </ClientLayout>
  );
}
